import { LDEvaluationDetail } from 'launchdarkly-react-client-sdk'
import type {
  FeatureFlag,
  BooleanFlag,
  ExperimentInput,
  ExperimentOutput
} from './feature-flag-types'
import { FeatureFlagClient } from './feature-flag-client'
import { UserContentSearch } from '@/domain/usecases/content/user-content-search'
import { ContentSummaryModel } from '@/domain/models/content/user-content-summary-model'
import { OriginAnalytics } from '../tracking'

/**
 * Alarga tipos literais boolean (true/false) para boolean.
 * Permite que flags com defaultValue: false sejam tipadas como boolean em vez de literal false.
 */
type WidenBoolean<T> = T extends boolean ? boolean : T

/**
 * Infere o tipo de uma feature flag a partir do seu defaultValue.
 * Flags boolean são alargadas para boolean; outros tipos são preservados.
 */
type InferFlagType<T> = T extends { readonly defaultValue: infer D } ? WidenBoolean<D> : unknown

/**
 * Lê o valor atual de uma feature flag via cliente LaunchDarkly.
 * Usa o cliente interno gerenciado pelo FeatureFlagClient — não requer passar ldClient.
 * Retorna o defaultValue se o cliente não estiver pronto.
 *
 * Uso em contextos não-React (guards, loaders, helpers puros).
 * Em componentes React, prefira `useFeatureFlags` para reatividade.
 *
 * @example
 * const isEnabled = getFlagValue(FeatureFlagName.I18N)
 *
 * @example
 * const companies = getFlagValue(FeatureFlagName.COMPANIES_WITH_CUSTOM_HELP_CENTER)
 */
export function getFlagValue<T extends FeatureFlag>(flag: T): InferFlagType<T>
export function getFlagValue(flag: FeatureFlag): unknown {
  const client = FeatureFlagClient.getClient()

  if (!client) {
    return flag.defaultValue
  }

  if (!FeatureFlagClient.isReady()) {
    console.warn(
      `[FeatureFlag] Cliente não pronto para '${flag.name}', usando default:`,
      flag.defaultValue
    )
    return flag.defaultValue
  }

  return client.variation(flag.name, flag.defaultValue)
}

/**
 * Verifica se uma flag boolean está habilitada.
 * Uso em contextos não-React. Em componentes, prefira `useFeatureFlags`.
 *
 * @example
 * const enabled = isFlagEnabled(FeatureFlagName.I18N)
 */
export function isFlagEnabled(flag: BooleanFlag): boolean {
  return getFlagValue(flag) as boolean
}

/**
 * Verifica se uma flag boolean está habilitada usando o cache local (síncrono).
 * Indicado para route loaders e guards onde o hook React não está disponível.
 *
 * @example
 * const enabled = checkFlagEnabled(FeatureFlagName.I18N)
 */
export function checkFlagEnabled(flag: FeatureFlag): boolean {
  const flagsMap = FeatureFlagClient.getFlagsMap()
  return (flagsMap?.[flag.name] ?? flag.defaultValue) as boolean
}

/**
 * Lê o valor de uma flag usando o cache local (síncrono).
 * Indicado para route loaders e guards onde o hook React não está disponível.
 *
 * @example
 * const companies = checkFlagValue(FeatureFlagName.COMPANIES_WITH_CUSTOM_HELP_CENTER)
 */
export function checkFlagValue<T extends FeatureFlag>(flag: T): InferFlagType<T>
export function checkFlagValue(flag: FeatureFlag): unknown {
  const flagsMap = FeatureFlagClient.getFlagsMap()
  return flagsMap?.[flag.name] ?? flag.defaultValue
}

/**
 * Verifica se o valor de uma flag de array contém um item específico.
 * Uso em contextos não-React. Em componentes, prefira `useFeatureFlags` + `.includes()`.
 *
 * @example
 * const temHelpCenter = flagIncludesValue(FeatureFlagName.COMPANIES_WITH_CUSTOM_HELP_CENTER, companyId)
 */
export function flagIncludesValue(flag: FeatureFlag, value: string | number): boolean {
  const flagValue = getFlagValue(flag)

  if (!Array.isArray(flagValue)) {
    console.error(`[FeatureFlag] Valor da flag '${flag.name}' não é um array:`, flagValue)
    return false
  }

  return flagValue.includes(value)
}

/**
 * Get variation detail for experiments
 *
 * @param flag - Feature flag definition
 * @returns Evaluation detail with reason and value
 */
export function getFlagVariationDetail(flag: FeatureFlag): LDEvaluationDetail {
  const client = FeatureFlagClient.getClient()

  if (!client) {
    return {
      value: flag.defaultValue
    }
  }

  return client.variationDetail(flag.name, flag.defaultValue)
}

/**
 * Check if LaunchDarkly is ready
 * Useful for preventing race conditions in loaders
 */
export function isFeatureFlagSystemReady(): boolean {
  return FeatureFlagClient.isReady()
}

/**
 * Map experiment value to variant name
 * @internal
 */
const mapExperimentValueToVariant = (value: boolean): string => {
  return value ? 'B' : 'A'
}

/**
 * Generate experiment data from evaluation detail
 *
 * @param input - LaunchDarkly evaluation detail
 * @returns Experiment output with variant info
 */
export function generateExperiments(input: ExperimentInput): ExperimentOutput {
  return {
    inExperiment: input.reason?.inExperiment ?? false,
    variantName: mapExperimentValueToVariant(input.value),
    kind: input.reason?.kind ?? 'UNKNOWN'
  }
}

export function getSearchResultTier({
  content,
  data,
  origin
}: {
  content?: ContentSummaryModel
  data?: UserContentSearch.Model
  origin?: OriginAnalytics
}): string {
  const page = content && data ? data.page : origin?.list?.page
  const listPosition =
    content && data ? data.data.findIndex(c => c.id === content.id) + 1 : origin?.list?.position

  if (page === 1 && listPosition !== undefined) {
    if (listPosition <= 1) return 'top-01'
    if (listPosition <= 3) return 'top-03'
    if (listPosition <= 6) return 'top-06'
    if (listPosition <= 12) return 'top-12'
    return 'top-24'
  }

  return `page-${page}`
}
